import { useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Plus, Save, ShieldCheck, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, type LoginStat, type PlatformSettingsOut } from "@/lib/api";

export const Route = createFileRoute("/_auth/admin/login-content")({
  component: AdminLoginContentPage,
});

type LoginFields = Pick<
  PlatformSettingsOut,
  "login_tagline" | "login_headline" | "login_subtitle" | "login_stats" |
  "login_compliance" | "login_register_tagline"
>;

const EMPTY_LOGIN: LoginFields = {
  login_tagline: "",
  login_headline: "",
  login_subtitle: "",
  login_stats: [],
  login_compliance: "",
  login_register_tagline: "",
};

function AdminLoginContentPage() {
  const qc = useQueryClient();
  const settings = useQuery({
    queryKey: ["admin", "platform-settings"],
    queryFn: () => api.admin.getPlatformSettings(),
  });

  const [form, setForm] = useState<LoginFields>(EMPTY_LOGIN);

  useEffect(() => {
    if (settings.data) {
      setForm({
        login_tagline: settings.data.login_tagline ?? "",
        login_headline: settings.data.login_headline ?? "",
        login_subtitle: settings.data.login_subtitle ?? "",
        login_stats: settings.data.login_stats ?? [],
        login_compliance: settings.data.login_compliance ?? "",
        login_register_tagline: settings.data.login_register_tagline ?? "",
      });
    }
  }, [settings.data]);

  const patch = useMutation({
    mutationFn: (body: Partial<PlatformSettingsOut>) => api.admin.patchPlatformSettings(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "platform-settings"] });
      qc.invalidateQueries({ queryKey: ["branding"] });
      toast.success("Contenido del login actualizado");
    },
  });

  function setField<K extends keyof LoginFields>(k: K, v: LoginFields[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  function setStat(i: number, field: keyof LoginStat, value: string) {
    setForm((f) => {
      const next = [...f.login_stats];
      next[i] = { ...next[i], [field]: value };
      return { ...f, login_stats: next };
    });
  }

  function addStat() {
    if (form.login_stats.length >= 4) return;
    setForm((f) => ({ ...f, login_stats: [...f.login_stats, { n: "", l: "" }] }));
  }

  function removeStat(i: number) {
    setForm((f) => ({ ...f, login_stats: f.login_stats.filter((_, idx) => idx !== i) }));
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    patch.mutate(form);
  }

  return (
    <div className="px-6 py-6 max-w-[1480px] mx-auto">
      <header className="mb-6">
        <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-ink-500 hover:text-ink-700 mb-2">
          <ArrowLeft size={14} /> Volver
        </Link>
        <h1 className="text-2xl font-bold text-ink-900 dark:text-white">Contenido de la página de login</h1>
        <p className="text-sm text-ink-500 mt-1">
          Edita el panel de marca visible al lado del formulario de login y registro.
        </p>
      </header>

      <div className="grid lg:grid-cols-[1fr_420px] gap-4">
        <Card>
          <CardHeader>
            <CardTitle>Textos editables</CardTitle>
            <CardDescription>Los cambios se aplican tras un refresh del navegador.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <Label>Tagline superior (uppercase)</Label>
                <Input
                  className="mt-1"
                  maxLength={200}
                  value={form.login_tagline ?? ""}
                  onChange={(e) => setField("login_tagline", e.target.value)}
                  placeholder="Inteligencia social para equipos"
                />
                <p className="text-[11px] text-ink-500 mt-1">Texto pequeño en mayúsculas sobre el headline.</p>
              </div>

              <div>
                <Label>Headline principal</Label>
                <textarea
                  className="mt-1 w-full min-h-[80px] rounded-lg border border-ink-200 dark:border-ink-700 bg-white dark:bg-ink-900 px-3 py-2 text-sm"
                  value={form.login_headline ?? ""}
                  onChange={(e) => setField("login_headline", e.target.value)}
                  placeholder="Escucha lo que se dice de tu marca, responde antes que se viralice."
                />
                <p className="text-[11px] text-ink-500 mt-1">
                  Soporta saltos de línea para quebrar manualmente. Texto grande visible al cargar.
                </p>
              </div>

              <div>
                <Label>Subtítulo / descripción</Label>
                <textarea
                  className="mt-1 w-full min-h-[80px] rounded-lg border border-ink-200 dark:border-ink-700 bg-white dark:bg-ink-900 px-3 py-2 text-sm"
                  value={form.login_subtitle ?? ""}
                  onChange={(e) => setField("login_subtitle", e.target.value)}
                  placeholder="Monitoriza menciones, automatiza respuestas y mide impacto…"
                />
              </div>

              <div className="border-t border-ink-200 dark:border-ink-800 pt-3">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <Label>Stats destacados</Label>
                    <p className="text-[11px] text-ink-500">Máximo 4 tarjetas (Número + Etiqueta).</p>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={addStat}
                    disabled={form.login_stats.length >= 4}
                  >
                    <Plus size={12} /> Añadir stat
                  </Button>
                </div>
                <div className="space-y-2">
                  {form.login_stats.length === 0 && (
                    <p className="text-xs text-ink-500 py-2">Sin stats. Añade hasta 4.</p>
                  )}
                  {form.login_stats.map((s, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Input
                        className="w-28"
                        value={s.n}
                        onChange={(e) => setStat(i, "n", e.target.value)}
                        placeholder="14M+"
                        maxLength={20}
                      />
                      <Input
                        className="flex-1"
                        value={s.l}
                        onChange={(e) => setStat(i, "l", e.target.value)}
                        placeholder="menciones / mes"
                        maxLength={80}
                      />
                      <button
                        type="button"
                        onClick={() => removeStat(i)}
                        className="w-8 h-8 rounded-md hover:bg-bad/10 text-bad flex items-center justify-center"
                        aria-label="Eliminar stat"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-t border-ink-200 dark:border-ink-800 pt-3">
                <Label>Línea de compliance / certificaciones</Label>
                <Input
                  className="mt-1"
                  maxLength={200}
                  value={form.login_compliance ?? ""}
                  onChange={(e) => setField("login_compliance", e.target.value)}
                  placeholder="SOC 2 Type II · GDPR · ISO 27001"
                />
                <p className="text-[11px] text-ink-500 mt-1">Aparece al pie del panel con icono de escudo.</p>
              </div>

              <div>
                <Label>Tagline del registro</Label>
                <Input
                  className="mt-1"
                  maxLength={200}
                  value={form.login_register_tagline ?? ""}
                  onChange={(e) => setField("login_register_tagline", e.target.value)}
                  placeholder="14 días de prueba — sin tarjeta de crédito"
                />
                <p className="text-[11px] text-ink-500 mt-1">Texto debajo del título "Crea tu cuenta".</p>
              </div>

              <div className="flex justify-end pt-2">
                <Button type="submit" disabled={patch.isPending}>
                  <Save size={14} /> {patch.isPending ? "Guardando…" : "Guardar"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Preview lado a lado */}
        <Card className="self-start lg:sticky lg:top-4">
          <CardHeader>
            <CardTitle>Preview</CardTitle>
            <CardDescription>Cómo se verá el panel de marca del login.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="rounded-xl overflow-hidden bg-gradient-to-br from-brand-700 via-brand-600 to-brand-800 text-white p-6 relative">
              <div className="absolute -bottom-16 -right-16 w-48 h-48 rounded-full bg-warn/30 blur-3xl pointer-events-none" />
              <div className="relative">
                {form.login_tagline && (
                  <p className="text-[10px] uppercase tracking-widest text-brand-100/80 mb-2">{form.login_tagline}</p>
                )}
                {form.login_headline && (
                  <h2 className="text-xl font-bold leading-tight whitespace-pre-line">{form.login_headline}</h2>
                )}
                {form.login_subtitle && (
                  <p className="mt-3 text-xs text-brand-100/90 leading-relaxed whitespace-pre-line line-clamp-4">
                    {form.login_subtitle}
                  </p>
                )}
                {form.login_stats.length > 0 && (
                  <div className="mt-4 grid grid-cols-3 gap-2">
                    {form.login_stats.map((s, i) => (
                      <div key={i} className="rounded-lg bg-white/10 backdrop-blur-sm ring-1 ring-white/15 px-2 py-2">
                        <div className="text-sm font-bold truncate">{s.n || "—"}</div>
                        <div className="text-[9px] text-brand-100/80 line-clamp-1">{s.l || "etiqueta"}</div>
                      </div>
                    ))}
                  </div>
                )}
                {form.login_compliance && (
                  <div className="mt-4 flex items-center gap-2 text-[11px] text-brand-100/80">
                    <ShieldCheck size={12} />
                    <span className="truncate">{form.login_compliance}</span>
                  </div>
                )}
              </div>
            </div>
            <p className="text-[11px] text-ink-500 mt-3">
              Última actualización:{" "}
              {settings.data?.updated_at ? new Date(settings.data.updated_at).toLocaleString("es-PE") : "—"}
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
